/**
 * Hook pour gérer le carnet d'adresses des sous-traitants
 * Migré vers React Query (useQuery + useMutation)
 * @module hooks/useSousTraitants
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/queryKeys';
import { invalidateSousTraitants } from '@/lib/queryInvalidation';
import type {
  SousTraitant,
  SousTraitantCreate,
  SousTraitantUpdate,
} from '@/types/sous-traitant';

// =============================================================================
// Types de retour du hook
// =============================================================================

export interface UseSousTraitantsReturn {
  /** Liste des sous-traitants */
  sousTraitants: SousTraitant[];
  /** Chargement en cours */
  isLoading: boolean;
  /** Erreur éventuelle */
  error: Error | null;
  /** Créer un sous-traitant */
  createSousTraitant: (data: SousTraitantCreate) => Promise<SousTraitant | null>;
  /** Mettre à jour un sous-traitant */
  updateSousTraitant: (id: string, data: SousTraitantUpdate) => Promise<boolean>;
  /** Supprimer un sous-traitant */
  deleteSousTraitant: (id: string) => Promise<boolean>;
  /** Récupérer un sous-traitant par ID */
  getSousTraitantById: (id: string) => SousTraitant | undefined;
  /** Recharger les données */
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook useSousTraitants
// =============================================================================

/**
 * Hook pour gérer les sous-traitants d'une entreprise
 * @param entrepriseId - ID de l'entreprise
 * @param onlyActifs - Ne charger que les ST actifs (défaut: true)
 */
export function useSousTraitants(
  entrepriseId: string | null,
  onlyActifs: boolean = true
): UseSousTraitantsReturn {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const enabled = !!entrepriseId;

  // ============================================================================
  // QUERY
  // ============================================================================

  const { data: sousTraitants = [], isLoading, error, refetch } = useQuery({
    queryKey: [...queryKeys.sousTraitants.all(entrepriseId!), onlyActifs ? 'actifs' : 'tous'],
    queryFn: async () => {
      let query = supabase
        .from('sous_traitants')
        .select('*')
        .eq('entreprise_id', entrepriseId!)
        .order('nom', { ascending: true });

      if (onlyActifs) {
        query = query.eq('statut', 'actif');
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      return (data as SousTraitant[]) || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 min (carnet d'adresses change rarement)
  });

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  // Créer un sous-traitant
  const createMutation = useMutation({
    mutationFn: async (data: SousTraitantCreate) => {
      const { data: newST, error: insertError } = await supabase
        .from('sous_traitants')
        .insert({
          ...data,
          statut: 'actif',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      return newST as SousTraitant;
    },
    onSuccess: (_data, variables) => {
      toast({
        title: 'Sous-traitant créé',
        description: `${variables.nom} a été ajouté au carnet d'adresses`,
      });
      invalidateSousTraitants(queryClient, entrepriseId!);
    },
    onError: (err) => {
      console.error('[useSousTraitants] Erreur création:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de créer le sous-traitant',
        variant: 'destructive',
      });
    },
  });

  // Mettre à jour un sous-traitant
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: SousTraitantUpdate }) => {
      const { error: updateError } = await supabase
        .from('sous_traitants')
        .update(data)
        .eq('id', id);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      toast({
        title: 'Sous-traitant mis à jour',
        description: 'Les modifications ont été enregistrées',
      });
      invalidateSousTraitants(queryClient, entrepriseId!);
    },
    onError: (err) => {
      console.error('[useSousTraitants] Erreur modification:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le sous-traitant',
        variant: 'destructive',
      });
    },
  });

  // Supprimer un sous-traitant
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error: deleteError } = await supabase
        .from('sous_traitants')
        .delete()
        .eq('id', id);

      if (deleteError) {
        // Si le ST a des contrats liés, on ne peut pas le supprimer
        if (deleteError.code === '23503') {
          toast({
            title: 'Suppression impossible',
            description:
              'Ce sous-traitant a des contrats en cours. Passez-le en inactif à la place.',
            variant: 'destructive',
          });
          return false;
        }
        throw deleteError;
      }
      return true;
    },
    onSuccess: (deleted) => {
      if (deleted) {
        toast({
          title: 'Sous-traitant supprimé',
          description: 'Le sous-traitant a été supprimé du carnet d\'adresses',
        });
        invalidateSousTraitants(queryClient, entrepriseId!);
      }
    },
    onError: (err) => {
      console.error('[useSousTraitants] Erreur suppression:', err);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le sous-traitant',
        variant: 'destructive',
      });
    },
  });

  // ============================================================================
  // WRAPPERS (garder l'API identique)
  // ============================================================================

  const createSousTraitant = useCallback(
    async (data: SousTraitantCreate): Promise<SousTraitant | null> => {
      try {
        return await createMutation.mutateAsync(data);
      } catch {
        return null;
      }
    },
    [createMutation]
  );

  const updateSousTraitant = useCallback(
    async (id: string, data: SousTraitantUpdate): Promise<boolean> => {
      try {
        await updateMutation.mutateAsync({ id, data });
        return true;
      } catch {
        return false;
      }
    },
    [updateMutation]
  );

  const deleteSousTraitant = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await deleteMutation.mutateAsync(id);
        return result ?? false;
      } catch {
        return false;
      }
    },
    [deleteMutation]
  );

  // Récupérer un ST par ID (depuis le cache local)
  const getSousTraitantById = useCallback(
    (id: string): SousTraitant | undefined => {
      return sousTraitants.find((st) => st.id === id);
    },
    [sousTraitants]
  );

  return {
    sousTraitants,
    isLoading,
    error: error as Error | null,
    createSousTraitant,
    updateSousTraitant,
    deleteSousTraitant,
    getSousTraitantById,
    refetch: async () => { await refetch(); },
  };
}
